import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_DataRouterContext, unstable_useBlocker as useBlocker } from 'react-router-dom';

/**
 * Navigation state that lets one in-app navigation through without asking,
 * for flows that already confirmed with the user (e.g. Mark as Done).
 */
export const SKIP_UNSAVED_GUARD = 'skipUnsavedGuard';

export const UNSAVED_CHANGES_MESSAGE = 'You have unsaved report changes. Leave this page and discard them?';

function NavigationBlocker({ when, message }) {
  const shouldBlock = useCallback(({ currentLocation, nextLocation }) => (
    when
    && !nextLocation.state?.[SKIP_UNSAVED_GUARD]
    && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
  ), [when]);
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (!when || window.confirm(message)) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, when, message]);

  return null;
}

/**
 * Asks before an in-app navigation (links, back button, navigate()) leaves a
 * page with unsaved changes. Blocking needs the app's data router; outside
 * one (tests, bare MemoryRouter) this renders nothing. Tab close / reload is
 * covered separately by a beforeunload listener.
 */
export default function UnsavedChangesGuard({ when, message = UNSAVED_CHANGES_MESSAGE }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  if (!dataRouter) return null;
  return <NavigationBlocker when={Boolean(when)} message={message} />;
}
